import { i18n, type Locales } from "@lingui/core";

/*
  Memoized internationalization functions
  These functions provide similar caching functionality to the date and number formatters in Lingui.
  License: MIT
*/
export const defaultLocale = "en";

function normalizeLocales(locales: Locales): string[] {
  const out = Array.isArray(locales) ? locales : [locales];
  return [...out, defaultLocale];
}

/** Memoized cache */
const cache = new Map<string, unknown>();

function getMemoized<T>(getKey: () => string, construct: () => T) {
  const key = getKey();

  let formatter = cache.get(key) as T;

  if (!formatter) {
    formatter = construct();
    cache.set(key, formatter);
  }

  return formatter;
}

function cacheKey(
  type: string,
  locales: readonly string[],
  options?: Intl.DateTimeFormatOptions | Intl.NumberFormatOptions,
) {
  const localeKey = locales.join("-");
  return `${type}-${localeKey}-${JSON.stringify(options)}`;
}

/* ============ */

/**
 * Formats a list of strings into a localized string using the specified separator and format options.
 *
 * @param items - An array of strings to be formatted.
 * @param lastStringSeparator - The separator to use between the last two items in the list. Can be "and" or "or".
 * @param format - Optional. An object containing formatting options for the list. Defaults to { style: "long", type: "conjunction" or "disjunction" based on lastStringSeparator }.
 * @returns A localized string representing the formatted list.
 */
export function i18nList(items: string[], lastStringSeparator: "and" | "or", format?: Intl.ListFormatOptions): string {
  const currentListFormat: Intl.ListFormatOptions = {
    style: "long",
    type: lastStringSeparator === "and" ? "conjunction" : "disjunction",
    ...format,
  };

  const currentLocales = normalizeLocales(i18n.locales ?? []);

  const formatter = getMemoized(
    () => cacheKey("list", currentLocales, currentListFormat),
    () => new Intl.ListFormat(currentLocales, currentListFormat),
  );

  return formatter.format(items);
}

export function i18nRelativeTimeFormat(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  options?: Intl.RelativeTimeFormatOptions,
): string {
  const currentLocales = normalizeLocales(i18n.locales ?? []);

  const formatter = getMemoized(
    () => cacheKey("relativeTimeFormat", currentLocales, options),
    () => new Intl.RelativeTimeFormat(currentLocales, options),
  );

  return formatter.format(value, unit);
}
